/* eslint-disable @typescript-eslint/ban-types */
import { Action, Dispatch } from "redux";
import { ExtractAddSlice } from "../ExtractAdd";
import { ExtractMetadataKey } from "../../RightPanel/ExtractDetail/Constant";
import { ITitleValidationInfo } from "./ExtractAdd.logic";
import { ExtractModes, ICreateExtractState, IErrorDetails, IExtract } from "./redux/Types";
import { SaveExtract } from "./Service";
import { ExtractCreateOrUpdateProps, ExtractOnSubmitProps, SaveExtractProps } from "./IExtractCreateOrUpdateProps";
/**
 * Get extract form filed validation and error text details
 * @param field - field type either title, survey or schema
 * @param translate - translation object for labels
 * @returns - Returns Error details on field type
 */
export const GetErrorDetails = (field: string, translate: Function): IErrorDetails => {
  const fieldName = {
    title: {
      InvalidErrorText: translate("Extract.FormErrors.TitleError"),
      maxLength: 200,
      maxLengthErrorText: translate("Extract.FormErrors.TitleLengthError")
    },
    schema: {
      InvalidErrorText: "",
      maxLength: 500,
      maxLengthErrorText: translate("Extract.FormErrors.SchemaLengthError")
    },
    survey: {
      InvalidErrorText: "",
      maxLength: 500,
      maxLengthErrorText: translate("Extract.FormErrors.SurveyLengthError")
    }
  };
  return fieldName[field];
};


/**
 * Get title validation info (Invlalid flag and error text)
 * @param value - selected value in title or schema or in survey name
 * @param name - field type either title, survey or schema
 * @param translate - translation object for labels
 * @returns - Returns validated Info as invalid and validation text
 */
export const IsInvalidateTitle = (value: string, name: string, translate: Function): ITitleValidationInfo => {
  value = value.trim();
  const data = GetErrorDetails(name, translate);
  if (!value && name === "title") {
    return {
      isInvalid: true,
      validationText: data.InvalidErrorText
    };
  }
  if (value.length > data.maxLength) {
    return {
      isInvalid: true,
      validationText: data.maxLengthErrorText
    };
  }
  return {
    isInvalid: false,
    validationText: ""
  };
};

/**
 * Common function to handle field changes except year range
 * @param e - details of event trigged when field changed
 * @param dispatch - dispatches the events
 * @param extractAdd - IcreateState contains extract details
 * @param t - translation object for labels
 * @param mode - extract modes of create, edit or view
 */
export const onFieldChange = (
  fieldName: string,
  fieldValidityState: ValidityState,
  e: React.SyntheticEvent<any, Event>,
  dispatch: Dispatch<Action>,
  extractAdd: ICreateExtractState,
  translate: Function,
  mode: ExtractModes
): void => {
  if(fieldName === ExtractMetadataKey.isactive || fieldName === ExtractMetadataKey.Description) {
    const val = fieldName === "isactive" && e.currentTarget.checked || e.target["value"];
    if (mode === ExtractModes.CREATE) {
      dispatch(ExtractAddSlice.actions.setNewExtract({
        ...extractAdd.newExtract,
        [fieldName]: val,
      }));
    }
    else if (mode === ExtractModes.EDIT) {
      dispatch(ExtractAddSlice.actions.setEditExtractDetail({
        ...extractAdd.editExtractDetail,
        [fieldName]: val,
      }));
    }
  }
  if(fieldName === ExtractMetadataKey.Title ||
      fieldName === ExtractMetadataKey.schema ||
      fieldName === ExtractMetadataKey.survey
  ) {
    const val = e.target["value"];
    const { isInvalid, validationText } = IsInvalidateTitle(val, fieldName, translate);
    if (mode === ExtractModes.CREATE) {
      dispatch(ExtractAddSlice.actions.setNewExtract({
        ...extractAdd.newExtract,
        [fieldName]: {
          "value": val,
          "valid": isInvalid,
          "errorText": validationText
        }
      }));
    }
    else if (mode === ExtractModes.EDIT) {
      dispatch(ExtractAddSlice.actions.setEditExtractDetail({
        ...extractAdd.editExtractDetail,
        [fieldName]: {
          "value": val,
          "valid": isInvalid,
          "errorText": validationText
        }
      }));
    }
  }
};

/**
 * Onchange Event for Year range
 * @param selectedValue - selected range value
 * @param dispatch - dispatches the events
 * @param extractAdd - IcreateState contains extract details
 * @param mode - extract modes either create or edit or view
 */
export const yearChange = (
  selectedValue: string,
  dispatch: Dispatch<Action>,
  extractAdd: ICreateExtractState,
  mode: ExtractModes
): void => {
  if (mode === ExtractModes.CREATE) {
    dispatch(ExtractAddSlice.actions.setNewExtract({
      ...extractAdd.newExtract,
      financialYear: selectedValue,
    }));
  }
  else if (mode === ExtractModes.EDIT) {
    dispatch(ExtractAddSlice.actions.setEditExtractDetail({
      ...extractAdd.editExtractDetail,
      financialYear: selectedValue,
    }));
  }
};

/**
 * Callback to create extract
 * @param dispatch - dispatches the events
 * @param createProps - It is of ExtractCreateOrUpdateProps type 
 */
const onCreateExtract = (dispatch: Dispatch<Action>, createProps: ExtractCreateOrUpdateProps): void => {
  const extractDetails = createProps.extractAdd;
  const yearsState = createProps.year;
  const notification = createProps.notification;
  const user = createProps.user;
  const viewExtract = createProps.extractState;
  const saveExtractDetails: SaveExtractProps = {
    extractDetails,
    selectedYear: yearsState,
    notification,
    user,
    viewExtract,
    mode: createProps.mode
  };

  if (createProps.mode === ExtractModes.EDIT)
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.VIEW));
  else
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.CREATE));

  SaveExtract(dispatch, saveExtractDetails);
};

/**
 * Extract create or update callback
 * @param dispatch - dispatches the events
 * @param details - It is of ExtractOnSubmitProps type
 */
export const onSubmit = (dispatch: Dispatch<Action>, details: ExtractOnSubmitProps): void => {
  let newData: IExtract;
  const mode = details.mode;
  const extractAdd = details.extractAdd;
  const translation = details.translate;
  const extractData = mode === ExtractModes.CREATE ? extractAdd.newExtract : extractAdd.editExtractDetail;
  if (mode === ExtractModes.CREATE) {
    newData = { ...extractAdd.newExtract };
  }
  else {
    newData = { ...extractAdd.editExtractDetail };
  }
  Object.keys(extractData).forEach((key: string) => {
    if (extractData[key] && extractData[key].value !== undefined) {
      const val = extractData[key].value;
      const { isInvalid, validationText } = IsInvalidateTitle(val, key, translation);
      newData[key] = {
        "value": val.trim(),
        "valid": isInvalid,
        "errorText": validationText
      };
    }
  });
  dispatch(ExtractAddSlice.actions.setNewExtract(newData));
  const createProps: ExtractCreateOrUpdateProps = {
    extractAdd: newData,
    year: details.year,
    notification: details.notification,
    user: details.user,
    mode,
    extractState: details.extractState,
    onClose: details.onClose
  };
  onCreateExtract(dispatch, createProps);
};
